import React, { useState } from 'react';
import { Input, Form, FormGroup, Button, Label, Alert } from 'reactstrap';
import AuthService from '../Services/AuthService';
import UsersService from '../Services/UsersService';
import { useToasts } from 'react-toast-notifications';
import { withTranslation } from 'react-multi-lang';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faExclamationTriangle, faRightFromBracket } from "@fortawesome/free-solid-svg-icons";

const ForceChangePassword = ({ user, onPasswordChanged, t }) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const { addToast } = useToasts();
    const authService = new AuthService();
    const usersService = new UsersService();

    const isValidPassword = (password) => {
        if (!password || password.length < 8) return false;
        return /[a-z]/.test(password) && /[A-Z]/.test(password) && /[0-9]/.test(password);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        if (!isValidPassword(newPassword)) {
            setError(t('users.passwordComplexityError') || 'Ο κωδικός πρέπει να αποτελείται από τουλάχιστον 8 χαρακτήρες και να περιέχει τουλάχιστον ένα πεζό, ένα κεφαλαίο γράμμα και έναν αριθμό');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError(t('users.passwordsDontMatch') || 'Passwords do not match');
            return;
        }

        setIsLoading(true);
        usersService.changePassword(user.userId, newPassword, false).then(() => {
            setIsLoading(false);
            addToast(t('users.changePasswordSuccess') || 'Password changed successfully', { appearance: 'success', autoDismiss: true });
            const updatedUser = { ...user, mustChangePassword: false };
            authService.setUser(updatedUser);
            if (onPasswordChanged) {
                onPasswordChanged(updatedUser);
            }
        }).catch(err => {
            setIsLoading(false);
            const msg = typeof err === 'string' ? err : (err?.message || 'Error changing password');
            setError(msg);
            addToast(msg, { appearance: 'error', autoDismiss: true });
        });
    };

    const handleLogout = () => {
        authService.logout();
        addToast("Αποσυνδεθήκατε από το σύστημα", { appearance: 'warning', autoDismiss: true });
    };

    return (
        <div className="login-wrapper">
            <div className="login-card" style={{ maxWidth: '480px' }}>
                <div className="login-logo-container">
                    <img src="/logo512.png" alt="Nordia Logo" />
                    <h3 className="login-title text-danger d-flex align-items-center justify-content-center gap-2">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span>{t('users.forcePasswordChangeTitle') || 'Password Change Required'}</span>
                    </h3>
                    <p className="login-subtitle text-muted mt-2">
                        {t('users.forcePasswordChangeSubtitle') || 'An administrator has requested that you change your password before continuing.'}
                    </p>
                </div>

                {error && (
                    <Alert color="danger" className="py-2 small">
                        {error}
                    </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                    <FormGroup className="mb-3">
                        <Label htmlFor="forceNewPassword" className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faLock} className="text-muted small" />
                            <span>{t('users.newPassword') || 'New Password'}</span>
                        </Label>
                        <Input 
                            type="password" 
                            id="forceNewPassword"
                            placeholder="Εισάγετε νέο κωδικό (min 8 χαρακτήρες)"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)} 
                            required
                        />
                    </FormGroup>

                    <FormGroup className="mb-4">
                        <Label htmlFor="forceConfirmPassword" className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faLock} className="text-muted small" />
                            <span>{t('users.confirmPassword') || 'Confirm Password'}</span>
                        </Label>
                        <Input 
                            type="password" 
                            id="forceConfirmPassword"
                            placeholder="Επιβεβαιώστε νέο κωδικό"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)} 
                            required
                        />
                    </FormGroup>

                    <Button color="primary" block className="w-100 py-2 font-weight-bold mb-2" disabled={isLoading} type="submit">
                        {isLoading ? 'Αποθήκευση...' : (t('users.submitAndContinue') || 'Change Password & Continue')}
                    </Button>

                    <Button color="outline-secondary" block className="w-100 py-2 d-flex align-items-center justify-content-center gap-2" onClick={handleLogout} type="button">
                        <FontAwesomeIcon icon={faRightFromBracket} />
                        <span>{t('topBar.logout') || 'Logout'}</span>
                    </Button>
                </Form>
            </div>
        </div>
    );
};

export default withTranslation(ForceChangePassword);
